import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
  Button,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import BillingService from '../../../services/billingService';
import type { BillingRecord, BillingStatus } from '../../../types/billing';

interface BillingHistoryListProps {
  onError?: (error: string) => void;
}

const BillingHistoryList: React.FC<BillingHistoryListProps> = ({
  onError,
}) => {
  const [billingHistory, setBillingHistory] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // 請求履歴の取得
  const fetchBillingHistory = useCallback(async (currentPage = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await BillingService.getBillingHistory(currentPage, 10);
      
      if (response.success && response.data) {
        setBillingHistory(response.data);
        
        if (response.pagination) {
          setTotalPages(Math.ceil(response.pagination.total / response.pagination.limit));
        }
      } else {
        throw new Error(response.error?.message || '請求履歴の取得に失敗しました');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '請求履歴の取得中にエラーが発生しました';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  // 初回読み込み
  useEffect(() => {
    fetchBillingHistory(page);
  }, [page, fetchBillingHistory]);

  // 請求書ダウンロード
  const handleDownloadInvoice = async (record: BillingRecord) => {
    if (downloadingIds.has(record.id)) return;

    try {
      setDownloadingIds(prev => new Set(prev).add(record.id));
      
      const blob = await BillingService.downloadInvoice(record.id);
      
      // ダウンロードリンクを作成
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `請求書_${record.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      const errorMessage = '請求書のダウンロードに失敗しました';
      console.error('Download failed:', err);
      onError?.(errorMessage);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
    }
  };

  // ステータスチップの取得（シンプル化）
  const getStatusChip = (status: BillingStatus, paymentDate?: Date) => {
    // 支払済みかどうかで判定
    if (status === 'paid' || paymentDate) {
      return (
        <Chip
          label="Paid"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    }
    
    return (
      <Chip
        label="Unpaid"
        color="warning"
        size="small"
        variant="outlined"
      />
    );
  };

  // 日付フォーマット
  const formatDate = (date: Date) => {
    return format(new Date(date), 'yyyy年MM月dd日', { locale: ja });
  };

  // ページ変更ハンドラー
  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>請求履歴</Typography>
          </Box>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>請求履歴</Typography>
          </Box>
          <Alert 
            severity="error" 
            action={
              <Button size="small" onClick={() => fetchBillingHistory(page)}>
                再試行
              </Button>
            }
          >
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>請求履歴</Typography>
        </Box>

        {billingHistory.length === 0 ? (
          <Alert severity="info">
            請求履歴がありません
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>日付</TableCell>
                    <TableCell>説明</TableCell>
                    <TableCell align="right">金額</TableCell>
                    <TableCell align="center">ステータス</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingHistory.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(record.paymentDate || record.billingDate)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {record.items.length > 0 ? record.items[0].name : 'SalonHub プラン'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.invoiceNumber}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          ¥{record.totalAmount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        {getStatusChip(record.status, record.paymentDate)}
                      </TableCell>
                      
                      <TableCell align="center">
                        <Tooltip title="請求書をダウンロード">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadInvoice(record)}
                            disabled={downloadingIds.has(record.id)}
                          >
                            {downloadingIds.has(record.id) ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ページネーション */}
            {totalPages > 1 && (
              <Stack direction="row" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                />
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BillingHistoryList;